import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Login, FormValues, CurrentUser, AuthNextAction } from '../types';
import { authApi, ValidatorsProps, ValidatorResponse } from './services';

export interface AuthState {
  requestInProgress: boolean;
  validatorsIsLoading: boolean;
  isAuthenticated: boolean;
  formValues?: FormValues;
  user?: CurrentUser;
  userIsLoading?: boolean;
  userId?: string;
  loginValidation?: ValidatorsProps;
  nextAction?: AuthNextAction;
  mfaToken?: string;
  totpUri?: string;
  totpNotActive?: boolean;
}

const initialState: AuthState = {
  requestInProgress: false,
  validatorsIsLoading: false,
  isAuthenticated: false,
  formValues: undefined,
  user: undefined,
  userIsLoading: false,
  totpNotActive: false,
};

export const authSlice = createSlice({
  name: 'authenticate',
  initialState,
  reducers: {
    logout: (state) => {
      state.requestInProgress = false;
      state.validatorsIsLoading = false;
      state.isAuthenticated = false;
      state.formValues = undefined;
      state.user = undefined;
      state.userIsLoading = false;

      localStorage.removeItem('userId');
    },
    setTotpNotActive: (state) => {
      state.totpNotActive = true;
    },
    clearMfa: (state) => {
      state.nextAction = undefined;
      state.mfaToken = undefined;
      state.totpUri = undefined;
    },
    setIsAuthenticated: (state) => {
      state.isAuthenticated = true;
      state.totpNotActive = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.getValidators.matchPending, (state) => {
        state.validatorsIsLoading = true;
      })
      .addMatcher(
        authApi.endpoints.getValidators.matchFulfilled,
        (state, action: PayloadAction<ValidatorResponse>) => {
          state.validatorsIsLoading = false;
          state.loginValidation = action.payload.data.loginEmailSchema;
        },
      )
      .addMatcher(authApi.endpoints.getValidators.matchRejected, (state) => {
        state.validatorsIsLoading = false;
      })
      .addMatcher(authApi.endpoints.login.matchPending, (state) => {
        state.requestInProgress = true;
      })
      .addMatcher(
        authApi.endpoints.login.matchFulfilled,
        (state, action: PayloadAction<{ data: Login }>) => {
          state.requestInProgress = false;
          state.nextAction = action.payload.data.nextAction;

          if (action.payload.data.mfaToken) {
            state.mfaToken = action.payload.data.mfaToken;
          }
        },
      )
      .addMatcher(
        authApi.endpoints.generateTotp.matchFulfilled,
        (state, action: PayloadAction<{ data: Login }>) => {
          state.mfaToken = action.payload.data.mfaToken;

          if (action.payload.data.totpUri) {
            state.totpUri = action.payload.data.totpUri;
          }
        },
      )
      .addMatcher(
        authApi.endpoints.verify.matchFulfilled,
        (state, action: PayloadAction<{ data: { userId: string } }>) => {
          state.userId = action.payload.data.userId;
          state.isAuthenticated = true;

          localStorage.setItem('userId', JSON.stringify(action.payload.data));
        },
      )
      .addMatcher(
        authApi.endpoints.verify.matchRejected,
        // @ts-ignore
        (
          state,
          action: {
            meta: {
              baseQueryMeta: {
                response: {
                  headers: Headers;
                };
              };
            };
          },
        ) => {
          if (
            action.meta.baseQueryMeta.response.headers.get(
              'x-baseella-next-mfa-token',
            )
          ) {
            state.mfaToken = action.meta.baseQueryMeta.response.headers.get(
              'x-baseella-next-mfa-token',
            ) as string;
          }
        },
      )
      .addMatcher(authApi.endpoints.login.matchRejected, (state) => {
        state.requestInProgress = false;
      })
      .addMatcher(authApi.endpoints.getCurrentUser.matchPending, (state) => {
        state.userIsLoading = true;
      })
      .addMatcher(
        authApi.endpoints.getCurrentUser.matchFulfilled,
        (state, action: PayloadAction<{ data: CurrentUser }>) => {
          state.userIsLoading = false;
          state.user = action.payload.data;
        },
      )
      .addMatcher(authApi.endpoints.getCurrentUser.matchRejected, (state) => {
        state.userIsLoading = false;
      });
  },
});

export const { logout, setIsAuthenticated, clearMfa, setTotpNotActive } =
  authSlice.actions;

export default authSlice.reducer;
